// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";

import { IState } from "states/states";
import { IAction } from "actions";
import { isSet, roundToDigits, formatTimestamp } from "shared/utils/utils";
import { TimePeriod } from "types";
import { IInput, IOutput, OutputMode } from "shared/types/parameter";

import { getColor, getIoType, getSiteSelection, getGraphBoundaryDatesFromPeriod } from "guiutils";
import { IIoTrend, IIoTrendCount } from "states/trendingstates";
import IOsModel from "model/ios";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";
import { IDropdownSelectOption } from "./DropdownSelect";
import { Aggregation } from "graphql/api";

const DIGITS_TO_SHOW = 3;

interface IIoDatatableExtProps {
  aggregation: Aggregation;
}

interface IIoDatatableProps {
  isLoading: boolean;
  queryMoreData: boolean;
  selectedInputs: number[] | undefined;
  selectedOutputs: number[] | undefined;
  lastUpdate: number | undefined;
  ioTrend: IIoTrend;
  iotrendCount: IIoTrendCount;
  isIoTrendRestricted: boolean;
  graphStart: number | undefined;
  graphEnd: number | undefined;
  timePeriod: TimePeriod;
  selectedIoType: "analog" | "digital";
  showAllData: boolean;
  input: IInput[] | undefined;
  output: IOutput[] | undefined;
}

const mapStateToProps = (state: IState, ownProps: IIoDatatableExtProps) => {
  const selectedIoType = getIoType(state);
  const showAllData = ownProps.aggregation === Aggregation.NONE || selectedIoType === "digital";
  const siteSelection = getSiteSelection(state);
  const { graphStart, graphEnd, timePeriod } = getGraphBoundaryDatesFromPeriod(
    state.gui.ioPeriod,
    state.site.tzOffset,
  );
  const props: IIoDatatableProps = {
    isLoading: state.scheduler.isIoTrendLoading,
    queryMoreData: state.trending.queryMoreData,
    lastUpdate: state.scheduler.ioTrendLoading.lastData, // to ensure screen update
    selectedInputs: siteSelection?.inputs,
    selectedOutputs: siteSelection?.outputs,
    ioTrend: showAllData ? state.trending.ioTrendFull : state.trending.ioTrendAggr,
    iotrendCount: state.trending.ioTrendCount,
    isIoTrendRestricted: state.trending.isIoTrendRestricted,
    graphStart,
    graphEnd,
    timePeriod,
    showAllData,
    selectedIoType,
    input: state.para.parameter?.input,
    output: state.para.parameter?.output,
  };
  return props;
};

interface IIoDatatableDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoDatatableDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

interface ITimeStats {
  lastState: number | undefined;
  lastTimeStamp: number;
  timeOff: number;
  timeOn: number;
  sumTimOff: number;
  sumTimOn: number;
  check?: number;
}

class IoDatatableComponent extends React.Component<
  IIoDatatableExtProps & IIoDatatableProps & IIoDatatableDispatch
> {
  private startTime?: number;
  private endTime?: number;
  private selectedOptionsFlat: IDropdownSelectOption[] = [];
  private parameter?: (IInput | IOutput | undefined)[] = [];

  private getTimestamps() {
    this.startTime = this.props.graphStart;
    this.endTime = this.props.graphEnd;
    const dataTimestamps = Object.keys(this.props.ioTrend)
      .map((item) => parseInt(item))
      .filter((curItem) => curItem >= this.startTime! && curItem < this.endTime!);

    return dataTimestamps;
  }

  public render() {
    if (this.props.isLoading || this.props.queryMoreData) {
      return (
        <div className="loading-spinner-box">
          <div id="loading-spinner"></div>
        </div>
      );
    }

    const rows: any[] = [];
    const timestamps = this.getTimestamps();
    const isDigital = this.props.selectedIoType === "digital";

    const allIos = IOsModel.getAllAvailableIos(
      [this.props.input ?? [], this.props.output ?? []],
      this.props.selectedIoType,
    );
    const selectedOptions = IOsModel.getSelectedIos(
      allIos,
      this.props.selectedInputs,
      this.props.selectedOutputs,
      false,
    );
    this.selectedOptionsFlat = [...selectedOptions[0], ...selectedOptions[1]];

    this.parameter = this.selectedOptionsFlat.map((curItem) =>
      curItem.source === "input" ?
        this.props.input?.[curItem.index]
      : this.props.output?.[curItem.index],
    );

    // prepare a timestat instance for each selectedIo
    const timeStats = this.selectedOptionsFlat.map(() => {
      const timeStat: ITimeStats = {
        lastState: undefined,
        lastTimeStamp: 0,
        timeOff: 0,
        timeOn: 0,
        check: 0,
      } as ITimeStats;
      return timeStat;
    });

    timestamps.forEach((curTimestamp) => {
      this.selectedOptionsFlat.forEach((selectedIo, dataIndex) => {
        const sourceIndex = selectedIo.source === "input" ? 0 : 1;
        const parameter = this.parameter?.[dataIndex];
        const isInverted =
          selectedIo.source === "output" &&
          (parameter as IOutput | undefined)?.mode === OutputMode.OPEN;
        const timeStat = timeStats[dataIndex];
        // reset statistic times
        // (for each entry start at zero - do not accumulate!)
        timeStat.timeOn = 0;
        timeStat.timeOff = 0;

        let curValue = this.props.ioTrend[curTimestamp]?.[sourceIndex]?.[selectedIo.index] as
          number | undefined;
        if (isSet(curValue) && isInverted) {
          curValue = curValue ? 0 : 1;
        }

        if (curValue !== undefined) {
          // prepare timing-statistic --> only if digital and if all data is available!
          if (isDigital && this.props.showAllData) {
            // do some timing-statistics
            if (!isSet(timeStat.lastState)) {
              // find very first time-entry of the current io
              const firstTimestamp = Object.keys(this.props.ioTrend)
                .map((item) => parseInt(item))
                .filter((timestamp) => {
                  if (timestamp < this.startTime!) {
                    return (
                      this.props.ioTrend[timestamp]?.[sourceIndex]?.[selectedIo.index] !== undefined
                    );
                  }
                  return false;
                });

              // check if we found any...
              if (firstTimestamp.length > 0) {
                // if so -> load it -- if the value is undefined - leave state undefined (no sensor until now!)
                // set the first valid value to the timestamp available (this indicates, that the sensor was connected after
                // the startime of our graph!)
                timeStat.lastState =
                  (this.props.ioTrend[firstTimestamp[0]]?.[sourceIndex]?.[selectedIo.index] as
                    number | undefined) ?? undefined;
                if (isSet(timeStat.lastState) && isInverted) {
                  timeStat.lastState = timeStat.lastState ? 0 : 1;
                }
              }

              // check if we found state before the first point we want to add...
              if (isSet(timeStat.lastState)) {
                // if yes -> use that state from the graph-start-date to the current timestamp
                // check if the graph has a start-date
                if (isSet(this.startTime)) {
                  // set first value we want to add
                  timeStat.lastTimeStamp = this.startTime;
                  const dateFormat = new Date(timeStat.lastTimeStamp * 1000);
                  dateFormat.setHours(0);
                  dateFormat.setMinutes(0);
                  dateFormat.setSeconds(0);
                  timeStat.lastTimeStamp = dateFormat.valueOf() / 1000;
                }
              } else {
                // if no state was found - take the current one as the very first one ever available
                timeStat.lastState = curValue;
              }

              if (timeStat.lastTimeStamp === 0) {
                // there was not state bofore the frist timestamp availble OR there is no valid startDate in our Graph set...
                // first one indicates, that the sensor was conencted and this timestamp
                // holds the very first value. So start here with all calculations and
                // drawings
                // if there is no startDate in our graph available - take the found value as the first one
                timeStat.lastTimeStamp = curTimestamp;
              }
            }

            if (timeStat.lastState) {
              timeStat.timeOn = curTimestamp - timeStat.lastTimeStamp;
              timeStat.sumTimOn += timeStat.timeOn;
            } else {
              timeStat.timeOff = curTimestamp - timeStat.lastTimeStamp;
              timeStat.sumTimOff += timeStat.timeOff;
            }
            timeStat.lastTimeStamp = curTimestamp;
            timeStat.lastState = curValue;
          }

          const count =
            this.props.iotrendCount ?
              this.props.iotrendCount[curTimestamp] ?
                (this.props.iotrendCount[curTimestamp][sourceIndex]?.[selectedIo.index] as
                  number | undefined)
              : -1
            : -10;
          const color =
            this.selectedOptionsFlat.length === 1 ? "black" : getColor(selectedIo.index);

          rows.push(
            <tr key={`${curTimestamp}_${parameter?.name}_${selectedIo.index}`} style={{ color }}>
              <td className="nowrap">
                {!this.props.showAllData && [TimePeriod.YEAR].includes(this.props.timePeriod) && (
                  <>
                    {<FormattedDate valueMs={curTimestamp * 1000} longFormat={true} noDay={true} />}
                  </>
                )}
                {(this.props.showAllData || ![TimePeriod.YEAR].includes(this.props.timePeriod)) && (
                  <> {<FormattedDate valueMs={curTimestamp * 1000} />} </>
                )}
                {(this.props.showAllData ||
                  [TimePeriod.DAY, TimePeriod.WEEK].includes(this.props.timePeriod)) && (
                  <>
                    {" "}
                    {
                      <FormattedTime
                        valueMs={curTimestamp * 1000}
                        showSeconds={this.props.showAllData}
                      />
                    }{" "}
                  </>
                )}
              </td>
              <td className="nowrap">
                <span className="nowrap">{parameter?.name}</span>
              </td>
              <td className={isDigital ? "center" : "right"}>
                {isDigital && this.props.showAllData ?
                  curValue ?
                    <FormattedMessage id="On" />
                  : <FormattedMessage id="Off" />
                : isDigital ?
                  roundToDigits(count, DIGITS_TO_SHOW)
                : <>
                    {roundToDigits(curValue, DIGITS_TO_SHOW)}
                    {Boolean((parameter as IInput | undefined)?.unit) && (
                      <span className="ms-1">{(parameter as IInput).unit}</span>
                    )}
                  </>
                }
              </td>
              {isDigital && this.props.showAllData && (
                <td className={isDigital ? "center" : "right"}>
                  {formatTimestamp(timeStat.timeOff)}
                </td>
              )}
              {isDigital && this.props.showAllData && (
                <td className={isDigital ? "center" : "right"}>
                  {formatTimestamp(timeStat.timeOn)}
                </td>
              )}
            </tr>,
          );
        }
      });
    });

    if (rows.length === 0) {
      return (
        <div className="datatable">
          <p className="ms-1">
            <FormattedMessage id="No data available." />
          </p>
        </div>
      );
    }

    return (
      <>
        <table className="datatable fullwidthwhite">
          <thead>
            <tr>
              <th>
                <FormattedMessage id="Timestamp" />
              </th>
              <th>
                <FormattedMessage id="Name" />
              </th>
              <th className={isDigital ? "center" : "right"}>
                {isDigital ?
                  this.props.showAllData ?
                    <FormattedMessage id="New State" />
                  : <FormattedMessage id="Activities" />
                : <FormattedMessage id="Value" />}
              </th>

              {isDigital && this.props.showAllData && (
                <th className={isDigital ? "center" : "right"}>
                  <FormattedMessage id="Off Time" />
                </th>
              )}
              {isDigital && this.props.showAllData && (
                <th className={isDigital ? "center" : "right"}>
                  <FormattedMessage id="On Time" />
                </th>
              )}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
        {this.props.isIoTrendRestricted && (
          <div className="alert alert-danger mt-2">
            <FormattedMessage id="The number of input/output trend data items is very high." />{" "}
            <FormattedMessage id="Therefore the data table might be truncated and not complete." />
          </div>
        )}
      </>
    );
  }
}

const IoDatatable = connect(mapStateToProps, mapDispatchToProps)(IoDatatableComponent);

export default IoDatatable;
